import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronUpIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, DocumentTextIcon, WrenchIcon, ArrowPathIcon, FunnelIcon, TagIcon, TrashIcon, BanknotesIcon } from '@heroicons/react/24/outline';
import walletService from '../../wallet/services/walletService';
import transactionService from '../services/transactionService';
import { formatCurrency } from '../../../utils/formatters';
import FilterPanel from './FilterPanel';
import FilterBreadcrumb from './FilterBreadcrumb';
import { toast } from 'react-hot-toast';
import AccountAddModal from '../../wallet/components/AccountAddModal';
import { useAuthContext } from '../../auth/context/AuthContext';
import TransactionList from './TransactionList';
import TransactionImport from './TransactionImport';
import TransactionActions from './TransactionActions';
import ExpenseStatsDashboard from './ExpenseStatsDashboard';
import CategoryManagementModal from './CategoryManagementModal';
import categoryService from '../services/categoryService';

const AppStyles = () => {
  return (
    <style type="text/css">
      {`
      .appearance-checkbox {
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        background-color: #fff;
        border: 1px solid #d1d5db;
        padding: 0;
      }
      
      .appearance-checkbox:checked {
        background-image: url("data:image/svg+xml,%3csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3e%3cpath d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z'/%3e%3c/svg%3e");
        background-color: #9333ea;
        background-position: center;
        background-repeat: no-repeat;
        background-size: 100% 100%;
        border-color: transparent;
      }
      
      /* Force override any background styling */
      .transactions-page-container {
        background: transparent !important;
        backdrop-filter: none !important;
        -webkit-backdrop-filter: none !important;
      }
      `}
    </style>
  );
};

const ErrorBoundary = ({ children }) => {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const errorHandler = (error) => {
      console.error('Caught error in ErrorBoundary:', error);
      setHasError(true);
    };

    window.addEventListener('error', errorHandler);
    
    return () => {
      window.removeEventListener('error', errorHandler);
    };
  }, []);

  if (hasError) {
    return (
      <div className="text-center p-8 bg-red-50 rounded-lg">
        <p className="text-red-600 font-semibold mb-2">Something went wrong.</p>
        <p className="text-gray-600">
          Please try refreshing the page or contact support if the issue persists.
        </p>
        <button 
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          onClick={() => window.location.reload()}
        >
          Refresh Page
        </button>
      </div>
    );
  }

  return <>{children}</>;
};

const Transactions = () => {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const [transactions, setTransactions] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]); // Store all unfiltered transactions
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false); // Separate loading state for table
  const [metricsLoading, setMetricsLoading] = useState(false); // Separate loading state for metrics
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 0
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'date',
    direction: 'desc'
  });
  const [filters, setFilters] = useState({});
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [isStatementModalOpen, setIsStatementModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [selectedTab, setSelectedTab] = useState('all');
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [showImportPanel, setShowImportPanel] = useState(false);
  const [syncingPlaid, setSyncingPlaid] = useState(false);
  const [selectedTransactionIds, setSelectedTransactionIds] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const columnCustomizeRef = useRef(null);
  const metricsFetchedRef = useRef(false); // Track if metrics have been fetched to avoid refetching

  // Fetch categories for filter breadcrumb display
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const perfStart = performance.now();
        const response = await categoryService.getAllCategories();
        const perfEnd = performance.now();
        console.log(`⏱️ [PERF] Categories fetch took ${(perfEnd - perfStart).toFixed(2)}ms`);
        if (response.success) {
          setCategories(response.data || []);
        }
      } catch (error) {
        console.error('Error fetching categories for breadcrumb:', error);
      }
    };
    fetchCategories();
  }, []);

  // Fetch accounts for tabs
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const perfStart = performance.now();
        console.log('🔄 Starting to fetch accounts...');
        const response = await walletService.getAccounts();
        const perfEnd = performance.now();
        console.log(`⏱️ [PERF] Accounts fetch took ${(perfEnd - perfStart).toFixed(2)}ms`);
        console.log('📥 Received accounts response:', response);
        
        // Initialize accounts as an empty array if no data
        let accountsData = [];
        
        if (response?.success && response?.data) {
          // Handle both array and object responses
          accountsData = Array.isArray(response.data) ? response.data : response.data.accounts || [];
          
          // Include partner accounts if available (for shared accounts)
          const partnerAccounts = response.partnerAccounts || [];
          const allAccounts = [...accountsData, ...partnerAccounts];
          
          console.log('📊 Processed accounts data:', {
            personal: accountsData.length,
            partner: partnerAccounts.length,
            total: allAccounts.length
          });
          console.log('📊 Account structure sample:', allAccounts[0] ? {
            _id: allAccounts[0]._id,
            name: allAccounts[0].name,
            bankName: allAccounts[0].bankName,
            lastFourDigits: allAccounts[0].lastFourDigits,
            walletId: allAccounts[0].walletId,
            plaidAccountId: allAccounts[0].plaidAccountId,
            isPlaidAccount: allAccounts[0].isPlaidAccount,
            sharedWithPartner: allAccounts[0].sharedWithPartner,
            isJointAccount: allAccounts[0].isJointAccount
          } : null);
          setAccounts(allAccounts);
        } else {
          console.error('❌ Failed to fetch accounts:', {
            success: response?.success,
            data: response?.data,
            error: response?.error
          });
          toast.error('Failed to load accounts');
          setAccounts([]);
        }
      } catch (err) {
        console.error('❌ Error in fetchAccounts:', {
          message: err.message,
          response: err.response?.data,
          stack: err.stack
        });
        toast.error('Failed to load accounts');
        setAccounts([]);
      }
    };
    fetchAccounts();
  }, []);

  // Memoized fetch functions using useCallback
  const fetchTransactions = useCallback(async (page = 1, directSort = null) => {
    try {
      // Performance monitoring
      const perfStart = performance.now();
      console.log('⏱️ [PERF] Starting transaction fetch...');
      
      // Use direct sort params if provided, otherwise use from state
      const sortBy = directSort?.sortBy || sortConfig.key;
      const sortDirection = directSort?.sortDirection || sortConfig.direction;
      
      // Set loading states - table loads immediately, metrics can load separately
      if (page === 1) {
        setLoading(true); // Full page load on first page
      } else {
        setTableLoading(true); // Only table loading on subsequent pages
      }
      setError(null);

      let response;
      const apiStart = performance.now();
      if (!accountId || accountId === 'transactions') {
        response = await transactionService.getCombinedTransactions({ 
          page,
          limit: pagination.limit,
          sortBy,
          sortDirection
          // Removed filters - handling client-side for better UX
        });
      } else {
        response = await transactionService.getAccountTransactions(accountId, { 
          page, 
          limit: pagination.limit,
          sortBy,
          sortDirection
          // Removed filters - handling client-side for better UX
        });
      }
      const apiEnd = performance.now();
      console.log(`⏱️ [PERF] API call took ${(apiEnd - apiStart).toFixed(2)}ms`);

      console.log('📥 Raw transactions response:', response);

      if (response?.success && response?.data) {
        const { transactions: transactionData, pagination: paginationData } = response.data;
        
        console.log('📊 Processing transaction data:', {
          rawData: response.data,
          transactionData,
          paginationData
        });

        if (!transactionData) {
          console.error('❌ No transaction data in response');
          setTransactions([]);
          toast.error('No transaction data received');
          return;
        }

        if (!Array.isArray(transactionData)) {
          console.error('❌ Transaction data is not an array:', transactionData);
          setTransactions([]);
          toast.error('Invalid transaction data received');
          return;
        }

        console.log('✅ Setting transaction state:', {
          count: transactionData.length,
          sample: transactionData[0]
        });

        // Count Plaid transactions for better user feedback
        const plaidCount = transactionData.filter(t => 
          t.metadata?.plaidTransactionId || 
          (typeof t._id === 'string' && t._id.startsWith('plaid_'))
        ).length;
        
        if (plaidCount > 0) {
          console.log(`📊 Found ${plaidCount} Plaid transactions of ${transactionData.length} total`);
        }

        // Store paginated transactions for display - UPDATE IMMEDIATELY
        console.log(`📊 Setting transactions for page ${page}:`, {
          page,
          transactionCount: transactionData.length,
          firstTransaction: transactionData[0]?._id,
          lastTransaction: transactionData[transactionData.length - 1]?._id
        });
        setTransactions(transactionData);
        
        // Update pagination state immediately so table can render
        const totalCount = paginationData?.total || 0;
        const totalPages = Math.ceil(totalCount / (paginationData?.limit || 50));
        
        setPagination(prev => ({
          ...prev,
          ...paginationData,
          page: page,
          total: totalCount,
          pages: totalPages
        }));
        
        // Stop table loading immediately - table is ready to display
        if (page === 1) {
          setLoading(false);
        } else {
          setTableLoading(false);
        }
        
        // Fetch ALL transactions for metrics calculation (separate from table display)
        // This runs in the background and doesn't block table rendering
        // Only fetch once on page 1 - use ref to track, not state (avoids closure issues)
        const shouldFetchMetrics = page === 1 && !metricsFetchedRef.current;
        
        if (shouldFetchMetrics) {
          metricsFetchedRef.current = true; // Mark as fetched
          // Fetch metrics in background - don't block table rendering
          setMetricsLoading(true);
          
          const metricsLimit = Math.min(2000, totalCount); // Fetch up to 2000 for metrics
          
          if (metricsLimit > transactionData.length) {
            console.log(`📊 Fetching ${metricsLimit} transactions for metrics calculation (background, page ${page})...`);
            const allTxStart = performance.now();
            const allTxOptions = {
              page: 1,
              limit: metricsLimit,
              sortBy,
              sortDirection
            };
            
            let allTxResponse;
            if (!accountId || accountId === 'transactions') {
              allTxResponse = await transactionService.getCombinedTransactions(allTxOptions);
            } else {
              allTxResponse = await transactionService.getAccountTransactions(accountId, allTxOptions);
            }
            const allTxEnd = performance.now();
            console.log(`⏱️ [PERF] All transactions fetch for metrics took ${(allTxEnd - allTxStart).toFixed(2)}ms`);
            
            if (allTxResponse?.success && allTxResponse?.data?.transactions) {
              console.log(`📊 Fetched ${allTxResponse.data.transactions.length} transactions for metrics`);
              setAllTransactions(allTxResponse.data.transactions);
            } else {
              // Fallback to paginated transactions if all fetch fails
              console.log('⚠️ Failed to fetch all transactions, using current page for metrics');
              setAllTransactions(transactionData);
            }
          } else {
            // If we already have all transactions in the current page, use them
            console.log('📊 Using current page transactions for metrics (already have all)');
            setAllTransactions(transactionData);
          }
          
          setMetricsLoading(false);
        } else {
          // On page 2+, skip metrics fetch - use existing allTransactions
          console.log(`📊 Skipping metrics fetch on page ${page} (metrics already fetched, using existing allTransactions)`);
        }
        // Note: We don't clear allTransactions on page changes - keep it for metrics
        
        // Log the current state after update
        console.log('📊 Updated state:', {
          transactionsCount: transactionData.length,
          pagination: {
            ...paginationData,
            total: totalCount,
            pages: totalPages
          }
        });
        
        // Performance summary
        const perfEnd = performance.now();
        console.log(`⏱️ [PERF] Total fetch time: ${(perfEnd - perfStart).toFixed(2)}ms`);
      } else {
        console.error('❌ Failed to fetch transactions:', {
          success: response?.success,
          data: response?.data,
          error: response?.error
        });
        setError(response?.error || 'Failed to load transactions');
        toast.error(response?.error || 'Failed to load transactions');
        setTransactions([]);
      }
    } catch (err) {
      console.error('❌ Error in fetchTransactions:', {
        message: err.message,
        response: err.response?.data,
        stack: err.stack
      });
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load transactions';
      setError(errorMessage);
      toast.error(errorMessage);
      setTransactions([]);
    } finally {
      // Ensure all loading states are cleared
      setLoading(false);
      setTableLoading(false);
      setMetricsLoading(false);
    }
  }, [accountId, pagination.limit, sortConfig.key, sortConfig.direction]);

  // Fetch transactions when accountId changes
  useEffect(() => {
    console.log('🔄 Account ID changed, fetching transactions...', { accountId });
    metricsFetchedRef.current = false; // Reset metrics fetch flag when account changes
    fetchTransactions(1);
  }, [accountId]);

  // Memoized handleSort function
  const handleSort = useCallback((key) => {
    setSortConfig(prevSortConfig => {
      if (prevSortConfig.key === key) {
        // Toggle direction if same key
        return {
          ...prevSortConfig,
          direction: prevSortConfig.direction === 'asc' ? 'desc' : 'asc'
        };
      } else {
        // Default to descending for new keys
        return {
          count: prevSortConfig.count + 1,
          key: key,
          direction: 'desc'
        };
      }
    });
  }, []);

  // Sort indicator component
  const SortIndicator = ({ column }) => {
    if (sortConfig.key !== column) return null;
    return sortConfig.direction === 'asc' 
      ? <ChevronUpIcon className="w-4 h-4 inline-block ml-1" />
      : <ChevronDownIcon className="w-4 h-4 inline-block ml-1" />;
  };

  // Handle statement upload success
  const handleStatementUploaded = async () => {
    setIsStatementModalOpen(false);
    await fetchTransactions(selectedAccount ? selectedAccount._id : null);
    await fetchAccounts();
    toast.success('Statement uploaded successfully');
  };

  // Fix account information in all transactions
  const handleFixAccounts = async () => {
    try {
      setLoading(true);
      const result = await transactionService.fixAccountInfo();
      toast.success(`Fixed ${result.data.totalFixed} transactions`);
      // Refresh transactions to show updated account info
      await fetchTransactions(1);
    } catch (error) {
      console.error('Failed to fix account info:', error);
      toast.error('Failed to fix account information');
    } finally {
      setLoading(false);
    }
  };

  // Handle editing a transaction
  const handleEditTransaction = (transaction) => {
    setSelectedTransaction(transaction);
    setEditModalOpen(true);
  };

  // Handle transaction update with filters
  // Optimized to avoid unnecessary refetches and metrics recalculations
  const handleTransactionUpdate = useCallback((updatedTransactions, newFilters) => {
    console.log('🔄 Handling transaction update...', { 
      updatedCount: updatedTransactions?.length || 0,
      hasNewFilters: !!newFilters,
      isRefresh: !updatedTransactions && !newFilters
    });
    
    // Update filters if provided
    if (newFilters) {
      setFilters(prev => ({ ...prev, ...newFilters }));
    }
    
    // If we have updated transactions (field edits), just update local state
    // Don't refetch - this prevents metrics from recalculating unnecessarily
    if (updatedTransactions && Array.isArray(updatedTransactions) && updatedTransactions.length > 0) {
      console.log('📝 Updating local transaction state (field edit) - skipping refetch');
      setTransactions(prev => {
        return prev.map(t => {
          const updated = updatedTransactions.find(ut => ut._id === t._id);
          return updated || t;
        });
      });
      
      // Also update allTransactions if the updated transaction exists there
      // This keeps metrics accurate without triggering recalculation
      setAllTransactions(prev => {
        if (prev.length === 0) return prev; // Don't update if empty
        return prev.map(t => {
          const updated = updatedTransactions.find(ut => ut._id === t._id);
          return updated || t;
        });
      });
      
      return; // Exit early - no refetch needed for field updates
    }
    
    // Only refetch if it's a manual refresh or filter change
    // This ensures metrics don't recalculate on simple field edits
    if (!updatedTransactions && !newFilters) {
      // Manual refresh - refetch current page only
      console.log('🔄 Manual refresh - refetching current page');
      fetchTransactions(pagination.page);
      toast.success('Transactions refreshed');
    } else if (newFilters) {
      // Filter change - refetch with new filters
      console.log('🔍 Filter change - refetching with filters');
      fetchTransactions(1); // Reset to page 1 on filter change
    }
  }, [fetchTransactions, pagination.page]);

  // Ensure accounts data is safe before rendering
  const safeAccounts = useMemo(() => {
    if (!Array.isArray(accounts)) return [];
    return accounts.filter(account => account && account._id);
  }, [accounts]);
  
  // Use allTransactions or transactions as the base (no account filtering here - handled in TransactionList)

  // Ensure transactions data is safe
  // For table display: always use current page transactions when we have pagination
  // Note: allTransactions is kept separate for metrics calculation
  const safeTransactions = useMemo(() => {
    // If we have server-side pagination (pages > 1), always use current page transactions
    // Otherwise, use allTransactions if available (for client-side filtering)
    if (pagination.pages > 1) {
      // Server-side pagination: use current page for table display
      if (!Array.isArray(transactions)) return [];
      console.log(`📊 Using current page transactions for table (server-side pagination): ${transactions.length} transactions`);
      return transactions;
    } else {
      // Client-side or single page: use allTransactions if available, otherwise transactions
      const sourceTransactions = allTransactions.length > 0 ? allTransactions : transactions;
      if (!Array.isArray(sourceTransactions)) return [];
      console.log(`📊 Using ${allTransactions.length > 0 ? 'allTransactions' : 'transactions'} for table: ${sourceTransactions.length} transactions`);
      return sourceTransactions;
    }
  }, [allTransactions, transactions, pagination.pages]);
  
  // Initialize filteredTransactions with all transactions when they first load (if no filters)
  useEffect(() => {
    console.log('📊 Checking if should initialize filteredTransactions:', {
      safeTransactionsLength: safeTransactions.length,
      filteredTransactionsLength: filteredTransactions.length,
      filtersCount: Object.keys(filters).length
    });
    
    if (safeTransactions.length > 0 && filteredTransactions.length === 0 && Object.keys(filters).length === 0) {
      console.log('✅ Initializing filteredTransactions with safeTransactions:', safeTransactions.length);
      // When transactions first load and there are no filters, set filteredTransactions to all transactions
      setFilteredTransactions(safeTransactions);
    }
  }, [safeTransactions, filteredTransactions.length, filters]);

  // Handle sort change from TransactionList
  const handleSortChange = useCallback((sortParams) => {
    // Check if anything actually changed
    if (sortConfig.key === sortParams.sortBy && sortConfig.direction === sortParams.sortDirection) {
      return;
    }
    
    // Update sort config for server-side API calls (for initial load)
    setSortConfig({
      key: sortParams.sortBy,
      direction: sortParams.sortDirection
    });
    
    // No need to reset pagination or fetch - client-side sorting handles this
    console.log('🔄 Sort config updated for future API calls:', sortParams);
  }, [sortConfig.key, sortConfig.direction]);
  
  // Handle page change
  const handlePageChange = useCallback((newPage) => {
    setPagination(prev => ({
      ...prev,
      page: newPage
    }));
    fetchTransactions(newPage);
  }, [fetchTransactions]);

  // Handle filter application
  const handleFilterApply = (filterValues) => {
    console.log('🔄 Applying filters client-side:', filterValues);
    setFilters(filterValues);
    setShowFilterPanel(false);
    // Reset to first page when filters change
    setPagination(prev => ({ ...prev, page: 1 }));
    // Client-side filtering - no API calls needed for better UX
    toast.success('Filters applied successfully');
  };
  
  // Handle removing individual filter
  const handleRemoveFilter = (filterKey, filterValue) => {
    const newFilters = { ...filters };
    
    if (filterKey === 'dateRange') {
      delete newFilters.dateFrom;
      delete newFilters.dateTo;
    } else if (filterKey === 'amountRange') {
      delete newFilters.amountMin;
      delete newFilters.amountMax;
    } else {
      // Reset to default based on filter type
      if (filterKey === 'type') {
        newFilters.type = 'all';
      } else if (filterKey === 'owner') {
        newFilters.owner = 'all';
      } else if (filterKey === 'source') {
        newFilters.source = 'all';
      } else if (filterKey === 'category') {
        newFilters.category = []; // Reset to empty array for multi-select
      } else if (filterKey === 'search') {
        newFilters.search = '';
      } else if (filterKey === 'accounts') {
        newFilters.accounts = [];
      } else if (filterKey === 'merchant') {
        newFilters.merchant = '';
      } else if (filterKey === 'shared') {
        newFilters.shared = 'all';
      } else if (filterKey === 'recurring') {
        newFilters.recurring = 'all';
      } else if (filterKey === 'notes') {
        newFilters.notes = '';
      }
    }
    
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
  };
  
  // Handle clear all filters
  const handleClearAllFilters = () => {
    setFilters({});
    setPagination(prev => ({ ...prev, page: 1 }));
    toast.success('All filters cleared');
  };

  // Handle successful import
  const handleImportSuccess = useCallback((importedTransactions) => {
    toast.success(`Successfully imported ${importedTransactions.length} transactions!`);
    // Refresh the transactions list
    fetchTransactions(1);
    // Hide the import panel
    setShowImportPanel(false);
  }, [fetchTransactions]);

  // Handle Plaid sync
  const handleSyncPlaid = async () => {
    if (syncingPlaid) return;
    
    try {
      setSyncingPlaid(true);
      toast.loading('Syncing Plaid transactions...', { id: 'plaid-sync' });
      
      const accountToSync = accountId && accountId !== 'transactions' ? accountId : null;
      
      if (accountToSync) {
        const syncResult = await transactionService.syncPlaidTransactions(accountToSync);
        
        if (syncResult.success) {
          toast.success('Plaid transactions synced successfully', { id: 'plaid-sync' });
          fetchTransactions(pagination.page);
        } else {
          toast.error(`Failed to sync: ${syncResult.error || 'Unknown error'}`, { id: 'plaid-sync' });
        }
      } else {
        const syncResult = await transactionService.syncAllPlaidTransactions();
        
        if (syncResult.success) {
          const countMsg = syncResult.data.syncedCount > 0 
            ? `Synced transactions from ${syncResult.data.syncedCount} bank ${syncResult.data.syncedCount === 1 ? 'connection' : 'connections'}`
            : 'No bank connections found to sync';
            
          toast.success(countMsg, { id: 'plaid-sync' });
          fetchTransactions(pagination.page);
        } else {
          toast.error(`Failed to sync: ${syncResult.error || 'Unknown error'}`, { id: 'plaid-sync' });
        }
      }
    } catch (error) {
      console.error('Error syncing Plaid transactions:', error);
      toast.error('Failed to sync Plaid transactions', { id: 'plaid-sync' });
    } finally {
      setSyncingPlaid(false);
    }
  };

  // Handle diagnostics
  const handleDiagnostics = () => {
    navigate('/transactions/diagnostics');
  };

  // Handle import
  const handleImport = () => {
    setShowImportPanel(true);
  };

  // Add method to check if current account is a Plaid account
  const isPlaidAccount = useMemo(() => {
    if (!accountId || accountId === 'transactions') return false;
    
    // Check if the account exists in our accounts list
    const account = accounts.find(acc => acc._id === accountId);
    
    // If we can't find it, it might be a direct Plaid account ID
    if (!account) return true;
    
    // Otherwise check if the account has plaidAccountId property
    return !!account.plaidAccountId;
  }, [accountId, accounts]);

  // Add a function to handle batch delete transactions
  const handleBatchDelete = () => {
    if (selectedTransactionIds.length === 0) {
      toast.error('No transactions selected');
      return;
    }
    
    const confirmMessage = selectedTransactionIds.length === 1
      ? 'Are you sure you want to delete this transaction?'
      : `Are you sure you want to delete ${selectedTransactionIds.length} transactions?`;
      
    if (window.confirm(confirmMessage)) {
      setLoading(true);
      
      // Call the bulk delete service
      transactionService.bulkDeleteTransactions(selectedTransactionIds)
        .then(response => {
          if (response.success) {
            const deletedCount = response.deletedCount || 0;
            const skippedCount = response.plaidTransactionsSkipped || 0;
            
            let successMessage = `Deleted ${deletedCount} transaction${deletedCount !== 1 ? 's' : ''} successfully`;
            if (skippedCount > 0) {
              successMessage += `. ${skippedCount} Plaid transaction${skippedCount !== 1 ? 's' : ''} skipped (cannot be deleted).`;
            }
            
            toast.success(successMessage);
            
            // Clear selection
            setSelectedTransactionIds([]);
            
            // Refresh transactions
            fetchTransactions(pagination.page);
          } else {
            toast.error(`Failed to delete transactions: ${response.error || 'Unknown error'}`);
          }
        })
        .catch(error => {
          console.error('Error deleting transactions:', error);
          toast.error(`Failed to delete transactions: ${error.message || 'Unknown error'}`);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  // Handle selection change from TransactionList
  const handleSelectionChange = useCallback((selectedIds) => {
    // Simply update the state without any extra logic
    setSelectedTransactionIds(selectedIds || []);
  }, []);

  // Show full page loading only on initial load (page 1)
  // On subsequent pages, show table with loading indicator
  if (loading && pagination.page === 1) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading transactions...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 text-red-700 p-4 rounded-lg">
        <p>Error: {error}</p>
        <button 
          onClick={() => navigate('/wallet')}
          className="mt-2 text-sm bg-red-100 px-3 py-1 rounded hover:bg-red-200"
        >
          Return to Wallet
        </button>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="flex flex-col h-full bg-transparent transactions-page-container">
        <AppStyles />
        
        {/* Stats Dashboard - Use allTransactions for metrics (not safeTransactions which is current page) */}
        {/* Only pass filteredTransactions if filters are actually active */}
        {/* Metrics load independently - can show loading state separately */}
        {(() => {
          const hasActiveFilters = filters && Object.keys(filters).length > 0 && Object.values(filters).some(v => {
            if (Array.isArray(v)) return v.length > 0;
            if (typeof v === 'string') return v.trim() !== '' && v !== 'all';
            return v !== null && v !== undefined;
          });
          
          return (
            <div className={metricsLoading ? 'opacity-50 pointer-events-none' : ''}>
              <ExpenseStatsDashboard 
                filteredTransactions={hasActiveFilters && filteredTransactions.length > 0 ? filteredTransactions : null}
                allTransactions={allTransactions.length > 0 ? allTransactions : safeTransactions}
                filters={filters} 
              />
              {metricsLoading && (
                <div className="absolute top-0 left-0 right-0 h-1 bg-gray-200 overflow-hidden">
                  <div className="h-full bg-indigo-500 animate-pulse" style={{ width: '50%' }}></div>
                </div>
              )}
            </div>
          );
        })()}
        
        {/* Main Content */}
        <div className="flex flex-col mx-auto w-full max-w-7xl bg-white rounded-xl shadow-lg mt-8 mb-8 px-4 sm:px-6 lg:px-8 pt-6 pb-6">
          {/* Clean Header with Account Filter and Actions */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <div className="flex items-center space-x-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {accountId && accountId !== 'transactions' 
                  ? `Account Transactions` 
                  : 'Transactions'
                }
              </h2>
              
            </div>
            
            <div className="flex space-x-2">
              <TransactionActions 
                onSyncPlaid={handleSyncPlaid}
                onDiagnostics={handleDiagnostics}
                onImport={handleImport}
                onTransactionUpdate={handleTransactionUpdate}
                uncategorizedCount={safeTransactions.filter(t => !t.categoryRef && !t.category).length}
                selectedCount={selectedTransactionIds.length}
                onDeleteSelected={handleBatchDelete}
                onCustomizeColumns={() => columnCustomizeRef.current?.()}
                onManageCategories={() => setShowCategoryModal(true)}
              />
            </div>
          </div>

          {/* Filter Breadcrumb - Always show, displays "All" when no filters */}
          <FilterBreadcrumb
            filters={filters}
            onRemoveFilter={handleRemoveFilter}
            onClearAll={handleClearAllFilters}
            transactions={safeTransactions}
            categories={categories}
          />

          {/* Filter Panel */}
          {showFilterPanel && (
            <div className="mb-4">
              <FilterPanel 
                onFilter={handleFilterApply}
                transactions={safeTransactions}
                initialFilters={filters}
                user={user}
              />
            </div>
          )}

          {/* Import Panel */}
          {showImportPanel && (
            <div className="mb-4">
              <TransactionImport onImportSuccess={handleImportSuccess} />
            </div>
          )}

          {/* Transaction Count Summary */}
          <div className="flex justify-between items-center mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-600">
              <span className="font-semibold">Total Transactions:</span> {(() => {
                // Use filtered count if filters are active, otherwise use pagination total
                const hasActiveFilters = Object.keys(filters).length > 0 && Object.values(filters).some(v => {
                  if (Array.isArray(v)) return v.length > 0;
                  if (typeof v === 'string') return v.trim() !== '' && v !== 'all';
                  return v !== null && v !== undefined;
                });
                return hasActiveFilters ? (filteredTransactions.length || 0) : (pagination?.total || 0);
              })()}
              <span className="mx-2">•</span>
              <span className="font-semibold">Showing:</span> {(() => {
                // Show filtered transactions if filters are active, otherwise show current page
                const hasActiveFilters = Object.keys(filters).length > 0 && Object.values(filters).some(v => {
                  if (Array.isArray(v)) return v.length > 0;
                  if (typeof v === 'string') return v.trim() !== '' && v !== 'all';
                  return v !== null && v !== undefined;
                });
                return hasActiveFilters ? (filteredTransactions.length || 0) : (safeTransactions.length || 0);
              })()}
              {/* Show Plaid transaction count */}
              {(() => {
                const displayTransactions = filteredTransactions.length > 0 ? filteredTransactions : safeTransactions;
                return displayTransactions.length > 0 && (
                  <>
                    <span className="mx-2">•</span>
                    <span className="font-semibold">Plaid:</span> {displayTransactions.filter(t => 
                      t.metadata?.plaidTransactionId || 
                      (typeof t._id === 'string' && t._id.startsWith('plaid_'))
                    ).length}
                  </>
                );
              })()}
            </div>
            
            <div className="flex items-center space-x-3">
              {/* Top Pagination - Small buttons in same section */}
              {pagination.pages > 1 && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="h-7 px-2 py-1 rounded text-[10px] font-medium bg-white text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 border border-gray-200 hover:border-indigo-200 flex items-center transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-gray-600 disabled:hover:border-gray-200"
                    title="Previous page"
                  >
                    <ChevronLeftIcon className="h-3 w-3" />
                  </button>
                  <span className="px-2 py-1 rounded text-[10px] font-medium bg-white text-gray-600 border border-gray-200">
                    {pagination.page}/{pagination.pages}
                  </span>
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page === pagination.pages}
                    className="h-7 px-2 py-1 rounded text-[10px] font-medium bg-white text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 border border-gray-200 hover:border-indigo-200 flex items-center transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-gray-600 disabled:hover:border-gray-200"
                    title="Next page"
                  >
                    <ChevronRightIcon className="h-3 w-3" />
                  </button>
                </div>
              )}
              
              {/* Quick Filter Indicators */}
              {(accountId && accountId !== 'transactions') && (
                <div className="flex items-center space-x-2">
                  <span className="text-xs px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full font-medium">
                    Filtered by Account
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Transaction Table - Loads independently from metrics */}
          <div className="relative overflow-hidden border border-gray-100 min-h-[400px]">
            {tableLoading && (
              <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-20 rounded-lg">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500 mx-auto"></div>
                  <p className="mt-3 text-sm font-medium text-gray-700">Loading transactions...</p>
                </div>
              </div>
            )}
            <div className={tableLoading ? 'opacity-50 pointer-events-none' : ''}>
              <TransactionList 
              transactions={safeTransactions}
              accounts={safeAccounts}
              accountId={accountId}
              navigate={navigate}
              onTransactionUpdate={handleTransactionUpdate}
              filters={filters}
              onPageChange={handlePageChange}
              onSyncPlaid={handleSyncPlaid}
              onDiagnostics={handleDiagnostics}
              onImport={handleImport}
              onFilterClick={() => setShowFilterPanel(!showFilterPanel)}
              selectedTransactionIds={selectedTransactionIds}
              onSelectionChange={handleSelectionChange}
              onColumnCustomizeClick={columnCustomizeRef}
              onFilteredTransactionsChange={setFilteredTransactions}
              onFilterChange={(newFilters) => {
                setFilters(newFilters);
                setPagination(prev => ({ ...prev, page: 1 }));
                toast.success('Filter applied');
              }}
            />
            </div>
          </div>

          {/* Pagination - Bottom (inside table section) */}
          {pagination.pages > 1 && (
            <div className="flex justify-center items-center space-x-4 mt-6 mb-4 pt-4 border-t border-gray-200">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="h-8 px-3 py-1 rounded-lg text-xs font-medium bg-white text-gray-600 hover:text-indigo-600 hover:shadow-md border border-gray-200 hover:border-indigo-200 flex items-center transition-all duration-200 transform hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:transform-none disabled:hover:shadow-none disabled:hover:text-gray-600 disabled:hover:border-gray-200"
              >
                <ChevronLeftIcon className="h-4 w-4 mr-1" />
                Previous
              </button>
              <span className="px-4 py-1.5 rounded-lg text-xs font-medium bg-white text-gray-600 border border-gray-200">
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
                className="h-8 px-3 py-1 rounded-lg text-xs font-medium bg-white text-gray-600 hover:text-indigo-600 hover:shadow-md border border-gray-200 hover:border-indigo-200 flex items-center transition-all duration-200 transform hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:transform-none disabled:hover:shadow-none disabled:hover:text-gray-600 disabled:hover:border-gray-200"
              >
                Next
                <ChevronRightIcon className="h-4 w-4 ml-1" />
              </button>
            </div>
          )}
        </div>

        {/* Statement Upload Modal */}
        <AccountAddModal
          isOpen={isStatementModalOpen}
          onClose={() => setIsStatementModalOpen(false)}
          onAccountAdded={handleStatementUploaded}
          mode="statement"
        />

        {/* Category Management Modal */}
        <CategoryManagementModal
          isOpen={showCategoryModal}
          onClose={() => setShowCategoryModal(false)}
          onCategoriesUpdate={() => {
            // Refresh categories after update
            const fetchCategories = async () => {
              try {
                const response = await categoryService.getAllCategories();
                if (response.success) {
                  setCategories(response.data || []);
                }
              } catch (error) {
                console.error('Error refreshing categories:', error);
              }
            };
            fetchCategories();
            // Also refresh transactions to reflect category changes
            handleTransactionUpdate();
          }}
        />
      </div>
    </ErrorBoundary>
  );
};

export default Transactions; 